import { useCallback, useEffect, useRef, useState } from "react";
import { useTicketApi } from "@/api/tickets";
import { ExtendedInfoList, isExtendedInfoJsonEmpty, parseExtendedInfo } from "@/components/extended-info-list";
import { Icon } from "@/components/icon";
import { Loader } from "@/components/motion/loader";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/locales/i18n-provider";
import { useRecordingContent } from "../../hooks/use-recording-content";
import { formatRecordingTime } from "../../utils/recording-time";
import { saveBlobAs } from "../../utils/ticket-attachments";
import { formatDateTime } from "../../utils/ticket-presentation";
import { type RecordingWaveformBar, RecordWaveProgressBar, readRecordingWaveform } from "../recording-wava-progress-bar";
import { LinkedEntityCard, type LinkedEntityRendererProps } from "./linked-entity-card";

export function CallRecordLinkedEntity({ ticketId, link }: LinkedEntityRendererProps) {
  const api = useTicketApi();
  const { t, intlLocale } = useI18n();
  const isChinese = intlLocale.startsWith("zh");
  const data = link.data;
  const from = typeof data?.fromUser === "string" ? data.fromUser : "—";
  const to = typeof data?.toUser === "string" ? data.toUser : "—";
  const createdTime = typeof data?.createdTime === "string" ? data.createdTime : null;
  const durationSeconds = typeof data?.durationSeconds === "number" ? data.durationSeconds : null;
  const durationLabel = durationSeconds === null ? "—" : formatRecordingTime(durationSeconds);
  const recording = link.contents?.find((content) => content.key === "recording");
  const loadRecording = useCallback((signal: AbortSignal) => api.downloadLinkedEntityContent(ticketId, link.id, "recording", signal), [api, ticketId, link.id]);
  const { getContent, loading } = useRecordingContent(loadRecording);
  const audioRef = useRef<HTMLAudioElement>(null);
  const mounted = useRef(false);
  const readingWaveform = useRef(false);
  const [waveform, setWaveform] = useState<RecordingWaveformBar[] | null>(null);
  const [recordingLoaded, setRecordingLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [starting, setStarting] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState<number | null>(null);
  const [error, setError] = useState<"load" | "play" | null>(null);
  const total = duration ?? Math.max(0, durationSeconds ?? 0);
  const busy = loading || starting;

  useEffect(() => {
    mounted.current = true;
    const pauseForOtherRecording = (event: Event) => {
      const audio = audioRef.current;
      if (event.target instanceof HTMLAudioElement && event.target !== audio && event.target.hasAttribute("data-recording-player")) {
        audio?.pause();
      }
    };
    document.addEventListener("play", pauseForOtherRecording, true);
    return () => {
      mounted.current = false;
      document.removeEventListener("play", pauseForOtherRecording, true);
      const audio = audioRef.current;
      audio?.pause();
      audio?.removeAttribute("src");
      audio?.load();
    };
  }, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    setError(null);
    setStarting(true);
    try {
      const content = await getContent();
      if (!mounted.current) return;
      setRecordingLoaded(true);
      if (audio.src !== content.url) {
        audio.src = content.url;
        audio.load();
      }
      if (!readingWaveform.current && waveform === null) {
        readingWaveform.current = true;
        void readRecordingWaveform(content.blob)
          .then((peaks) => {
            if (mounted.current) setWaveform(peaks);
          })
          .catch(() => {
            // Unsupported codecs can still play in <audio>; retain a plain seek bar.
            if (mounted.current) setWaveform([]);
          });
      }
      if (audio.ended) audio.currentTime = 0;
      try {
        await audio.play();
      } catch {
        if (mounted.current) setError("play");
      }
    } catch {
      if (mounted.current) setError("load");
    } finally {
      if (mounted.current) setStarting(false);
    }
  };

  const download = async () => {
    setError(null);
    try {
      const content = await getContent();
      if (mounted.current && recording) saveBlobAs(content.blob, recording.fileName);
    } catch {
      if (mounted.current) setError("load");
    }
  };

  if (link.resolutionStatus !== "resolved") {
    return (
      <LinkedEntityCard icon="tabler:phone" link={link} title={data ? `${from} → ${to}` : t("tickets.callRecord.type")} type={t("tickets.callRecord.type")} />
    );
  }

  return (
    <li className="rounded-xl border bg-card p-4">
      {/* 客服录音没有字幕来源；下方按钮和进度条提供可读标签。 */}
      {/* biome-ignore lint/a11y/useMediaCaption: A call recording has no transcript; playback controls are labelled below. */}
      <audio
        data-recording-player
        hidden
        preload="none"
        ref={audioRef}
        onDurationChange={(event) => {
          const value = event.currentTarget.duration;
          if (Number.isFinite(value) && value > 0) setDuration(value);
        }}
        onEnded={() => setPlaying(false)}
        onError={() => {
          setPlaying(false);
          setStarting(false);
          setError("play");
        }}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
      />
      <div className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Icon icon="tabler:phone" size={18} />
        </span>
        <div className="min-w-0 flex-1 wrap-break-word">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
            <span className="font-medium text-foreground">{isChinese ? "来电" : "From"}</span>
            <strong className="font-semibold text-foreground">{from}</strong>
            <span className="text-muted-foreground">
              → {t("tickets.callRecord.agent")} {to}
            </span>
          </div>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            <span>
              {t("tickets.callRecord.time")} {createdTime ? formatDateTime(createdTime, intlLocale) : "—"}
            </span>
            <span aria-hidden="true">·</span>
            <span>
              {t("tickets.callRecord.duration")} {durationLabel}
            </span>
            <span aria-hidden="true">·</span>
            <span>
              {isChinese ? "记录号" : "Record ID"} {link.externalId}
            </span>
          </p>
        </div>
        {recording && (
          <div className="flex shrink-0 items-center gap-2">
            <Button
              aria-label={playing ? t("tickets.recording.pause") : t("tickets.recording.play")}
              disabled={busy}
              size="icon-sm"
              type="button"
              variant="outline"
              onClick={() => void togglePlay()}
            >
              {busy ? (
                <Loader label={t("tickets.recording.loading")} size={16} />
              ) : (
                <Icon icon={playing ? "tabler:player-pause" : "tabler:player-play"} size={14} />
              )}
            </Button>
            <Button aria-label={t("tickets.recording.download")} disabled={busy} size="icon-sm" type="button" variant="outline" onClick={() => void download()}>
              <Icon icon="tabler:download" size={14} />
            </Button>
          </div>
        )}
      </div>
      {recording && (starting || recordingLoaded) && (
        <RecordWaveProgressBar
          canSeek={duration !== null}
          currentTime={currentTime}
          loading={(starting && !recordingLoaded) || (recordingLoaded && waveform === null)}
          total={total}
          waveform={waveform ?? []}
          onSeek={(position) => {
            if (audioRef.current) audioRef.current.currentTime = position;
            setCurrentTime(position);
          }}
        />
      )}
      {!recording ? <p className="mt-3 text-xs text-muted-foreground">{t("tickets.recording.unavailable")}</p> : null}
      {error ? (
        <p className="mt-2 text-xs text-destructive" role="alert">
          {t(error === "load" ? "tickets.recording.loadFailed" : "tickets.recording.playFailed")}
        </p>
      ) : null}
      {!isExtendedInfoJsonEmpty(link.extendedInfoJson) ? (
        <details className="mt-3 border-t pt-3">
          <summary className="cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground">{t("tickets.linked.viewExtendedData")}</summary>
          <ExtendedInfoList className="mt-2" entries={parseExtendedInfo(link.extendedInfoJson, t("extendedInfo.value"), t("extendedInfo.raw"))} />
        </details>
      ) : null}
    </li>
  );
}
